/**
 * BeezFy Trust Strip Premium Manager - VERSIONE COMPLETA
 * Sistema trust badges ultra-premium per il miglior tema fashion Shopify
 * Version: 3.0.0 - TOTALMENTE REVISIONATO E OTTIMIZZATO
 */

(function() {
  'use strict';

  class BeezfyTrustStripPremium {
    constructor() {
      this.strips = [];
      this.modals = new Map();
      this.carousels = new Map();
      this.tooltips = new Map();
      this.lazyLoadObserver = null;
      this.animationObserver = null;
      this.performanceMode = this.detectPerformanceMode();
      this.isMobile = window.innerWidth < 768;
      this.isTablet = window.innerWidth >= 768 && window.innerWidth < 990;
      this.currentBreakpoint = this.getBreakpoint();
      this.initialized = false;

      // Bind methods
      this.handleResize = this.debounce(this.handleResize.bind(this), 250);
      this.handleScroll = this.throttle(this.handleScroll.bind(this), 100);

      // Initialize
      this.init();
    }

    init() {
      if (this.initialized) return;

      // Wait for DOM ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize());
      } else {
        this.initialize();
      }
    }

    initialize() {
      try {
        // Find all trust strips
        const stripElements = document.querySelectorAll('.beezfy-trust-strip, .beezfy-trust-strip-premium');

        if (stripElements.length === 0) {
          console.warn('BeezFy Trust Strip: No trust strips found on page');
          return;
        }

        // Initialize each strip
        stripElements.forEach(strip => {
          this.initializeStrip(strip);
        });

        // Setup global event listeners
        this.setupGlobalListeners();

        // Initialize lazy loading
        if (!this.performanceMode.shouldOptimize) {
          this.initLazyLoad();
        }

        // Initialize animations
        if (!this.performanceMode.reducedMotion) {
          this.initAnimations();
        }

        // Track impressions for analytics
        this.trackImpressions();

        this.initialized = true;

        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('beezfy:truststrip:initialized', {
          detail: { strips: this.strips.length }
        }));

      } catch (error) {
        console.error('BeezFy Trust Strip: Initialization error:', error);
      }
    }

    initializeStrip(strip) {
      const stripId = strip.dataset.sectionId || this.generateId();
      const layout = strip.dataset.layout || 'bar';
      const placement = strip.dataset.placement || 'global';

      // Store strip reference
      this.strips.push({
        element: strip,
        id: stripId,
        layout: layout,
        placement: placement
      });

      // Initialize based on layout type
      switch(layout) {
        case 'carousel':
          this.initCarousel(strip, stripId);
          break;
        case 'chips':
        case 'bar':
          this.initScrollable(strip);
          break;
        case 'grid':
        case 'cards':
          this.initGrid(strip);
          break;
      }

      // Initialize interactive elements
      this.initModals(strip, stripId);
      this.initTooltips(strip, stripId);
      this.initClickableItems(strip);

      // Apply entrance effects
      if (!this.performanceMode.reducedMotion) {
        this.applyEntranceEffect(strip);
      }

      // Setup sticky mobile if needed
      if (placement === 'product' && this.isMobile) {
        this.initStickyMobile(strip);
      }
    }

    initCarousel(strip, stripId) {
      const carousel = strip.querySelector('.beezfy-trust-strip__carousel');
      if (!carousel) return;

      const track = carousel.querySelector('.beezfy-trust-strip__carousel-track');
      const items = track.querySelectorAll('.beezfy-trust-strip__item');
      const prevBtn = carousel.querySelector('.beezfy-trust-strip__carousel-prev');
      const nextBtn = carousel.querySelector('.beezfy-trust-strip__carousel-next');
      const dotsContainer = carousel.querySelector('.beezfy-trust-strip__carousel-dots');

      if (items.length === 0) return;

      // Carousel state
      const carouselState = {
        currentIndex: 0,
        itemsCount: items.length,
        itemsPerView: this.getItemsPerView(strip),
        autoplay: carousel.dataset.autoplay === 'true',
        duration: parseInt(carousel.dataset.duration) || 5000,
        intervalId: null,
        track: track,
        items: items
      };

      // Calculate items per view
      carouselState.maxIndex = Math.max(0, items.length - carouselState.itemsPerView);

      // Create dots
      if (dotsContainer && carouselState.maxIndex > 0) {
        this.createCarouselDots(dotsContainer, carouselState);
      }

      // Setup controls
      if (prevBtn && nextBtn) {
        prevBtn.addEventListener('click', () => this.carouselPrev(carouselState, dotsContainer));
        nextBtn.addEventListener('click', () => this.carouselNext(carouselState, dotsContainer));

        // Hide controls if not needed
        if (carouselState.maxIndex === 0) {
          prevBtn.style.display = 'none';
          nextBtn.style.display = 'none';
        }
      }

      // Setup autoplay
      if (carouselState.autoplay && carouselState.maxIndex > 0) {
        this.startCarouselAutoplay(carouselState, dotsContainer);

        // Pause on hover
        carousel.addEventListener('mouseenter', () => this.stopCarouselAutoplay(carouselState));
        carousel.addEventListener('mouseleave', () => this.startCarouselAutoplay(carouselState, dotsContainer));
      }

      // Setup touch/swipe for mobile
      if (this.isMobile) {
        this.setupCarouselTouch(carousel, carouselState, dotsContainer);
      }

      // Store carousel state
      this.carousels.set(stripId, carouselState);
    }

    getItemsPerView(strip) {
      const columns = getComputedStyle(strip);

      if (this.isMobile) {
        return parseInt(columns.getPropertyValue('--bfts-columns-mobile') || '1');
      } else if (this.isTablet) {
        return parseInt(columns.getPropertyValue('--bfts-columns-tablet') || '2');
      } else {
        return parseInt(columns.getPropertyValue('--bfts-columns-desktop') || '4');
      }
    }

    createCarouselDots(container, state) {
      container.innerHTML = '';

      const dotsCount = state.maxIndex + 1;

      for (let i = 0; i < dotsCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'beezfy-trust-strip__carousel-dot';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);

        if (i === 0) {
          dot.classList.add('active');
        }

        dot.addEventListener('click', () => {
          this.carouselGoTo(state, container, i);
        });

        container.appendChild(dot);
      }
    }

    carouselPrev(state, dotsContainer) {
      state.currentIndex = Math.max(0, state.currentIndex - 1);
      this.updateCarousel(state, dotsContainer);
    }

    carouselNext(state, dotsContainer) {
      state.currentIndex = Math.min(state.maxIndex, state.currentIndex + 1);
      this.updateCarousel(state, dotsContainer);
    }

    carouselGoTo(state, dotsContainer, index) {
      state.currentIndex = Math.max(0, Math.min(state.maxIndex, index));
      this.updateCarousel(state, dotsContainer);
    }

    updateCarousel(state, dotsContainer) {
      const offset = -(state.currentIndex * (100 / state.itemsPerView));
      state.track.style.transform = `translateX(${offset}%)`;

      // Update dots
      if (dotsContainer) {
        const dots = dotsContainer.querySelectorAll('.beezfy-trust-strip__carousel-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === state.currentIndex);
        });
      }

      // Dispatch event
      window.dispatchEvent(new CustomEvent('beezfy:carousel:slide', {
        detail: { index: state.currentIndex }
      }));
    }

    startCarouselAutoplay(state, dotsContainer) {
      if (state.intervalId) return;

      state.intervalId = setInterval(() => {
        state.currentIndex = (state.currentIndex + 1) % (state.maxIndex + 1);
        this.updateCarousel(state, dotsContainer);
      }, state.duration);
    }

    stopCarouselAutoplay(state) {
      if (state.intervalId) {
        clearInterval(state.intervalId);
        state.intervalId = null;
      }
    }

    setupCarouselTouch(carousel, state, dotsContainer) {
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      const handleTouchStart = (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
        carousel.style.cursor = 'grabbing';
      };

      const handleTouchMove = (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
      };

      const handleTouchEnd = () => {
        if (!isDragging) return;

        const diff = startX - currentX;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
          if (diff > 0) {
            this.carouselNext(state, dotsContainer);
          } else {
            this.carouselPrev(state, dotsContainer);
          }
        }

        isDragging = false;
        carousel.style.cursor = 'grab';
      };

      carousel.addEventListener('touchstart', handleTouchStart, { passive: true });
      carousel.addEventListener('touchmove', handleTouchMove, { passive: true });
      carousel.addEventListener('touchend', handleTouchEnd);
    }

    initScrollable(strip) {
      const grid = strip.querySelector('.beezfy-trust-strip__grid');
      if (!grid || !this.isMobile) return;

      // Add scroll indicators if needed
      const items = grid.querySelectorAll('.beezfy-trust-strip__item');
      if (items.length > 2) {
        grid.style.overflowX = 'auto';
        grid.style.scrollSnapType = 'x mandatory';

        items.forEach(item => {
          item.style.scrollSnapAlign = 'start';
        });
      }
    }

    initGrid(strip) {
      // Grid layout initialization if needed
      const grid = strip.querySelector('.beezfy-trust-strip__grid');
      if (!grid) return;

      // Apply responsive column adjustments
      this.updateGridColumns(grid);
    }

    updateGridColumns(grid) {
      const parent = grid.closest('.beezfy-trust-strip, .beezfy-trust-strip-premium');
      if (!parent) return;

      const columns = this.getItemsPerView(parent);
      const items = grid.querySelectorAll('.beezfy-trust-strip__item');

      // Adjust last row alignment for odd number of items
      if (items.length % columns !== 0 && !this.isMobile) {
        grid.style.justifyContent = 'center';
      }
    }

    initModals(strip, stripId) {
      const modalTriggers = strip.querySelectorAll('[data-modal-trigger]');

      modalTriggers.forEach(trigger => {
        const modalId = trigger.dataset.modalTrigger;
        const modal = document.getElementById(`trust-modal-${modalId}`);

        if (!modal) return;

        // Store modal reference
        this.modals.set(modalId, modal);

        // Setup trigger
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openModal(modal);
        });

        // Setup close buttons
        const closeBtn = modal.querySelector('.beezfy-trust-strip__modal-close');
        const overlay = modal.querySelector('.beezfy-trust-strip__modal-overlay');

        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closeModal(modal));
        }

        if (overlay) {
          overlay.addEventListener('click', () => this.closeModal(modal));
        }
      });
    }

    openModal(modal) {
      if (!modal) return;

      modal.classList.add('active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus management
      const firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Analytics
      this.trackEvent('modal_open', {
        modal_id: modal.id
      });
    }

    closeModal(modal) {
      if (!modal) return;

      modal.classList.remove('active');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Analytics
      this.trackEvent('modal_close', {
        modal_id: modal.id
      });
    }

    closeAllModals() {
      this.modals.forEach(modal => {
        this.closeModal(modal);
      });
    }

    initTooltips(strip, stripId) {
      const tooltipTriggers = strip.querySelectorAll('.beezfy-trust-strip__tooltip-trigger');

      tooltipTriggers.forEach(trigger => {
        const tooltip = trigger.querySelector('.beezfy-trust-strip__tooltip');
        if (!tooltip) return;

        // Desktop: hover
        if (!this.isMobile) {
          trigger.addEventListener('mouseenter', () => {
            tooltip.classList.add('visible');
          });

          trigger.addEventListener('mouseleave', () => {
            tooltip.classList.remove('visible');
          });
        } else {
          // Mobile: click
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Close other tooltips
            this.closeAllTooltips();

            // Toggle current
            tooltip.classList.toggle('visible');
          });
        }

        // Store tooltip reference
        const tooltipId = `${stripId}-tooltip-${this.tooltips.size}`;
        this.tooltips.set(tooltipId, tooltip);
      });
    }

    closeAllTooltips() {
      this.tooltips.forEach(tooltip => {
        tooltip.classList.remove('visible');
      });
    }

    initClickableItems(strip) {
      const clickableItems = strip.querySelectorAll('.beezfy-trust-strip__item--clickable');

      clickableItems.forEach(item => {
        // Add keyboard support
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'button');

        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            item.click();
          }
        });

        // Track clicks
        item.addEventListener('click', () => {
          const itemType = item.dataset.itemType;
          this.trackEvent('item_click', { item_type: itemType });
        });
      });
    }

    initStickyMobile(strip) {
      const stickyContainer = document.querySelector('.beezfy-trust-strip__sticky-mobile');
      if (!stickyContainer) return;

      // Show/hide based on scroll
      let lastScrollY = window.pageYOffset;

      const handleScroll = () => {
        const currentScrollY = window.pageYOffset;

        if (currentScrollY > 100) {
          if (currentScrollY < lastScrollY) {
            stickyContainer.classList.add('visible');
          } else {
            stickyContainer.classList.remove('visible');
          }
        } else {
          stickyContainer.classList.remove('visible');
        }

        lastScrollY = currentScrollY;
      };

      window.addEventListener('scroll', this.throttle(handleScroll, 100));
    }

    initLazyLoad() {
      const options = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      };

      this.lazyLoadObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const strip = entry.target;

            // Load images
            const lazyImages = strip.querySelectorAll('img[data-src]');
            lazyImages.forEach(img => {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              img.classList.add('loaded');
            });

            // Mark as loaded
            strip.classList.add('beezfy-trust-strip--loaded');

            // Stop observing
            this.lazyLoadObserver.unobserve(strip);
          }
        });
      }, options);

      // Observe strips with lazy loading
      document.querySelectorAll('.beezfy-trust-strip--lazy').forEach(strip => {
        this.lazyLoadObserver.observe(strip);
      });
    }

    initAnimations() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.2
      };

      this.animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const strip = entry.target;

            // Add animation class
            strip.classList.add('beezfy-trust-strip--animated');

            // Stop observing
            this.animationObserver.unobserve(strip);
          }
        });
      }, options);

      // Observe all strips
      this.strips.forEach(({ element }) => {
        if (!element.classList.contains('beezfy-trust-strip--animated')) {
          this.animationObserver.observe(element);
        }
      });
    }

    applyEntranceEffect(strip) {
      const effect = strip.dataset.entranceAnimation;
      if (!effect) return;

      strip.classList.add(`beezfy-trust-strip--animation-${effect}`);

      // Stagger items for certain effects
      if (effect === 'stagger') {
        const items = strip.querySelectorAll('.beezfy-trust-strip__item');
        items.forEach((item, index) => {
          item.style.setProperty('--item-index', index);
          item.style.animationDelay = `${index * 0.1}s`;
        });
      }
    }

    setupGlobalListeners() {
      // ESC key to close modals
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllModals();
          this.closeAllTooltips();
        }
      });

      // Click outside to close tooltips on mobile
      if (this.isMobile) {
        document.addEventListener('click', (e) => {
          if (!e.target.closest('.beezfy-trust-strip__tooltip-trigger')) {
            this.closeAllTooltips();
          }
        });
      }

      // Resize handler
      window.addEventListener('resize', this.handleResize);

      // Visibility change (pause animations)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAllAnimations();
        } else {
          this.resumeAllAnimations();
        }
      });
    }

    handleResize() {
      const newBreakpoint = this.getBreakpoint();

      if (newBreakpoint !== this.currentBreakpoint) {
        this.currentBreakpoint = newBreakpoint;
        this.isMobile = window.innerWidth < 768;
        this.isTablet = window.innerWidth >= 768 && window.innerWidth < 990;

        // Update carousels
        this.carousels.forEach((state, id) => {
          const strip = document.querySelector(`[data-section-id="${id}"]`);
          if (strip) {
            state.itemsPerView = this.getItemsPerView(strip);
            state.maxIndex = Math.max(0, state.items.length - state.itemsPerView);
            this.updateCarousel(state);
          }
        });

        // Update grids
        this.strips.forEach(({ element }) => {
          const grid = element.querySelector('.beezfy-trust-strip__grid');
          if (grid) {
            this.updateGridColumns(grid);
          }
        });
      }
    }

    handleScroll() {
      // Scroll-based functionality handled in specific methods
    }

    pauseAllAnimations() {
      this.carousels.forEach(state => {
        this.stopCarouselAutoplay(state);
      });
    }

    resumeAllAnimations() {
      this.carousels.forEach((state, id) => {
        if (state.autoplay) {
          const dotsContainer = document.querySelector(`[data-section-id="${id}"] .beezfy-trust-strip__carousel-dots`);
          this.startCarouselAutoplay(state, dotsContainer);
        }
      });
    }

    detectPerformanceMode() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const lowPower = navigator.getBattery && navigator.getBattery().then(battery => battery.level < 0.2);
      const slowConnection = navigator.connection &&
        (navigator.connection.saveData || navigator.connection.effectiveType === '2g');

      return {
        reducedMotion,
        lowPower,
        slowConnection,
        shouldOptimize: reducedMotion || slowConnection
      };
    }

    getBreakpoint() {
      const width = window.innerWidth;

      if (width < 400) return 'xs';
      if (width < 768) return 'sm';
      if (width < 990) return 'md';
      if (width < 1200) return 'lg';
      return 'xl';
    }

    trackImpressions() {
      // Track initial impressions
      this.strips.forEach(({ element, placement }) => {
        this.trackEvent('impression', {
          placement: placement,
          layout: element.dataset.layout
        });
      });
    }

    trackEvent(eventName, data = {}) {
      // Dispatch custom event for analytics
      window.dispatchEvent(new CustomEvent('beezfy:truststrip:event', {
        detail: {
          event: eventName,
          ...data,
          timestamp: Date.now()
        }
      }));

      // Also push to dataLayer if available (Google Analytics)
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'trust_strip_event',
          event_category: 'Trust Strip',
          event_action: eventName,
          event_label: data.placement || 'global',
          ...data
        });
      }
    }

    generateId() {
      return `trust-strip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    throttle(func, limit) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    }

    // Public API
    destroy() {
      // Clean up observers
      if (this.lazyLoadObserver) {
        this.lazyLoadObserver.disconnect();
      }

      if (this.animationObserver) {
        this.animationObserver.disconnect();
      }

      // Stop carousels
      this.carousels.forEach(state => {
        this.stopCarouselAutoplay(state);
      });

      // Remove event listeners
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('scroll', this.handleScroll);

      // Clear collections
      this.strips = [];
      this.modals.clear();
      this.carousels.clear();
      this.tooltips.clear();

      this.initialized = false;
    }

    refresh() {
      this.destroy();
      this.init();
    }
  }

  // Initialize on DOM ready
  if (typeof window.BeezfyTrustStripPremium === 'undefined') {
    window.BeezfyTrustStripPremium = new BeezfyTrustStripPremium();
  }

  // Also expose for legacy compatibility
  window.BeezfyTrustStrip = window.BeezfyTrustStripPremium;

})();